import {
  CRASH_SPEED_MS,
  HOUSE_EDGE,
  MAX_BET_KOPECKS,
  MAX_CRASH_MULTIPLIER_BP,
  MIN_BET_KOPECKS,
  ROUND_RESULT_SECONDS,
  ROUND_WAIT_SECONDS,
  TAU,
  currentMultiplier,
  fmtGameRub,
  formatMultiplier,
  multiplierAtMs
} from "./chunk-CX2J4RID.js";

// src/auth.ts
var STEP_UP_ACTIONS = [
  "balance.admin_adjust",
  "order.force_match",
  "fragment.session.update",
  "tbank.session.manage",
  "user.role.set",
  "config.economy.update",
  "config.games.update",
  "broadcast.send"
];
var STEP_UP_TTL_MS = {
  "balance.admin_adjust": 5 * 60 * 1e3,
  "order.force_match": 5 * 60 * 1e3,
  "fragment.session.update": 5 * 60 * 1e3,
  "tbank.session.manage": 5 * 60 * 1e3,
  "user.role.set": 5 * 60 * 1e3,
  "config.economy.update": 15 * 60 * 1e3,
  "config.games.update": 15 * 60 * 1e3,
  "broadcast.send": 15 * 60 * 1e3
};
function isStepUpAction(value) {
  return STEP_UP_ACTIONS.includes(value);
}

// src/brainrots.ts
var CASE_ONLY_LVL = 99;
var STARTER_BRAINROT_IDS = ["clown", "tang", "seahorse"];
var BRAINROTS = [
  { id: "clown", name: "\u041A\u043B\u043E\u0443\u043D\u0438", unlockLvl: 1, rarity: "\u041E\u0431\u044B\u0447\u043D\u044B\u0439", rc: "#ff7a3d", starter: true },
  { id: "tang", name: "\u0414\u043E\u0440\u0438-\u0411\u0440\u0438\u0437", unlockLvl: 1, rarity: "\u041E\u0431\u044B\u0447\u043D\u044B\u0439", rc: "#06b6d4", starter: true },
  { id: "seahorse", name: "\u041A\u043E\u0440\u0430\u043B\u043B-\u041A\u043E\u043D\u0451\u043A", unlockLvl: 1, rarity: "\u041E\u0431\u044B\u0447\u043D\u044B\u0439", rc: "#34d399", starter: true },
  { id: "fugu", name: "\u0424\u0443\u0433\u0443-\u041F\u0443\u0444\u0444", unlockLvl: 3, rarity: "\u0420\u0435\u0434\u043A\u0438\u0439", rc: "#38bdf8" },
  { id: "shark", name: "\u041D\u0435\u043E\u043D-\u0414\u0436\u0435\u0442", unlockLvl: 6, rarity: "\u042D\u043F\u0438\u0447\u0435\u0441\u043A\u0438\u0439", rc: "#a855f7" },
  { id: "koi", name: "\u0410\u0441\u0442\u0440\u043E-\u041A\u043E\u0438", unlockLvl: CASE_ONLY_LVL, rarity: "\u041B\u0435\u0433\u0435\u043D\u0434\u0430\u0440\u043D\u044B\u0439", rc: "#fbbf24" }
];
var LEGACY_MAP = {
  tungo: "clown",
  coffee: "fugu",
  croc: "shark",
  star: "koi"
};
var DEFAULT_BRAINROT = "clown";
function normalizeCompanionId(id) {
  if (!id) return DEFAULT_BRAINROT;
  return LEGACY_MAP[id] ?? id;
}
function isStarterBrainrot(id) {
  const norm = normalizeCompanionId(id);
  return STARTER_BRAINROT_IDS.includes(norm);
}
function getBrainrot(id) {
  const norm = normalizeCompanionId(id);
  return BRAINROTS.find((b) => b.id === norm);
}
function brainrotName(id) {
  return getBrainrot(id)?.name ?? "\u041A\u043B\u043E\u0443\u043D\u0438";
}
function isUnlocked(brainrot, level) {
  return level >= brainrot.unlockLvl;
}
function sahurStats(lvl) {
  return {
    power: 120 + lvl * 85,
    income: 12 + lvl * 9,
    multiplier: (1 + lvl * 0.15).toFixed(2)
  };
}

// src/cases.ts
var CASES = [
  {
    id: "common",
    name: "\u0420\u0438\u0444\u043E\u0432\u044B\u0439",
    priceGram: 2,
    priceRub: 2,
    variant: "common",
    loot: [
      { type: "rub", value: 0.5, weight: 22 },
      { type: "rub", value: 1, weight: 24 },
      { type: "rub", value: 2, weight: 14 },
      { type: "rub", value: 5, weight: 4 },
      { type: "xp", value: 150, weight: 10 },
      { type: "brainrot", value: "tang", weight: 6 },
      { type: "brainrot", value: "seahorse", weight: 6 },
      { type: "brainrot", value: "fugu", weight: 10 },
      { type: "brainrot", value: "shark", weight: 4 }
    ]
  },
  {
    id: "rare",
    name: "\u041F\u0438\u0440\u0430\u0442\u0441\u043A\u0438\u0439",
    priceGram: 5,
    priceRub: 5,
    variant: "rare",
    loot: [
      { type: "rub", value: 2, weight: 22 },
      { type: "rub", value: 4, weight: 22 },
      { type: "rub", value: 7, weight: 14 },
      { type: "rub", value: 15, weight: 6 },
      { type: "xp", value: 400, weight: 6 },
      { type: "brainrot", value: "fugu", weight: 8 },
      { type: "brainrot", value: "shark", weight: 12 },
      { type: "brainrot", value: "koi", weight: 10 }
    ]
  },
  {
    id: "legend",
    name: "\u0410\u0442\u043B\u0430\u043D\u0442\u0438\u0434\u0430",
    priceGram: 15,
    priceRub: 15,
    variant: "legend",
    loot: [
      { type: "rub", value: 8, weight: 22 },
      { type: "rub", value: 13, weight: 22 },
      { type: "rub", value: 22, weight: 14 },
      { type: "rub", value: 60, weight: 6 },
      { type: "xp", value: 1e3, weight: 4 },
      { type: "brainrot", value: "shark", weight: 12 },
      { type: "brainrot", value: "koi", weight: 20 }
    ]
  }
];
function getCase(id) {
  return CASES.find((c) => c.id === id);
}
function pickLoot(loot, r) {
  const total = loot.reduce((a, x) => a + x.weight, 0);
  let acc = Math.min(Math.max(r, 0), 0.999999999) * total;
  for (let i = 0; i < loot.length; i++) {
    if (acc < loot[i].weight) return { index: i, item: loot[i] };
    acc -= loot[i].weight;
  }
  const last = loot.length - 1;
  return { index: last, item: loot[last] };
}
function computeCaseStats(caseDef) {
  const totalWeight = caseDef.loot.reduce((acc, item) => acc + item.weight, 0);
  const basePrice = caseDef.priceGram || caseDef.priceRub;
  if (totalWeight <= 0 || basePrice <= 0) {
    return { totalWeight: 0, expectedGram: 0, expectedRubKopecks: 0, rtpPercent: 0 };
  }
  let expectedGram = 0;
  for (const item of caseDef.loot) {
    const prob = item.weight / totalWeight;
    if (item.type === "rub" && typeof item.value === "number") {
      expectedGram += prob * item.value;
    }
  }
  const roundedGram = Number(expectedGram.toFixed(4));
  const expectedRubKopecks = Math.round(expectedGram * 100);
  const rtpPercent = Number((expectedGram / basePrice * 100).toFixed(2));
  return {
    totalWeight,
    expectedGram: roundedGram,
    expectedRubKopecks,
    rtpPercent
  };
}

// src/pricing.ts
function computePrice(input) {
  const { qty, starPriceRub, feePercent } = input;
  const total = qty * starPriceRub;
  const fee = total * (feePercent / 100);
  const grand = total + fee;
  return { qty, starPriceRub, feePercent, total, fee, grand };
}
function toKopecks(rub) {
  return Math.round(rub * 100);
}
function kopecksToRub(kopecks) {
  return kopecks / 100;
}
function gramToMilli(gram) {
  return Math.round(gram * 1e3);
}
function milliToGram(milli) {
  return milli / 1e3;
}
function starsToMilliGram(stars, gramPerStar) {
  return Math.max(1, Math.round(stars * gramPerStar * 1e3));
}
function rubToMilliGram(amountRub, starPriceRub, gramPerStar) {
  const safeStarRub = Math.max(0.01, starPriceRub);
  const safeGramRate = Math.max(1e-4, gramPerStar);
  const stars = amountRub / safeStarRub;
  return Math.max(1, Math.ceil(stars * safeGramRate * 1e3));
}
function rubToStars(amountRub, starPriceRub) {
  const safeStarRub = Math.max(0.01, starPriceRub);
  return Math.max(1, Math.ceil(amountRub / safeStarRub));
}
function pickUniqueAmountKopecks(grandKopecks, used) {
  let cand = Math.max(1, Math.round(grandKopecks));
  for (let i = 0; i < 1e6; i++) {
    if (cand % 100 !== 0 && !used.has(cand)) return cand;
    cand++;
  }
  return cand;
}
var MATCH_OVERPAY_MAX_KOPECKS = 1e3;
var MATCH_UNDERPAY_MAX_KOPECKS = 10;
function isWithinMatchWindow(paymentKopecks, orderKopecks) {
  const delta = paymentKopecks - orderKopecks;
  return delta >= -MATCH_UNDERPAY_MAX_KOPECKS && delta <= MATCH_OVERPAY_MAX_KOPECKS;
}
var STARS_MIN = 50;
var QTY_LIMITS = { min: 1, max: 1e5 };
function clampQty(qty) {
  if (!Number.isFinite(qty)) return QTY_LIMITS.min;
  return Math.max(QTY_LIMITS.min, Math.min(QTY_LIMITS.max, Math.floor(qty)));
}
function computeRetailStarPricing(input) {
  const safeMarkup = Number.isFinite(input.markup) ? Math.max(1, input.markup) : 1.1;
  const safeGram = Math.max(1e-4, input.baseStarGram);
  const safeUsd = Math.max(1e-4, input.baseStarUsd);
  const baseRubRaw = input.paymentMethod === "usdt_ton" ? safeUsd * Math.max(1, input.usdRub) : safeGram * Math.max(1, input.gramRub);
  const baseRub = Number(baseRubRaw.toFixed(4));
  const retailRub = Number((baseRubRaw * safeMarkup).toFixed(4));
  const retailGram = Number((safeGram * safeMarkup).toFixed(6));
  return { baseRub, retailRub, retailGram };
}
function computeRetailPremiumTier(input) {
  const safeMarkup = Number.isFinite(input.markup) ? Math.max(1, input.markup) : 1.1;
  const safeGram = Math.max(0.01, input.baseGram);
  const safeUsd = Math.max(0.01, input.baseUsd);
  const safeStarGram = Math.max(1e-4, input.baseStarGram);
  const baseRubRaw = input.paymentMethod === "usdt_ton" ? safeUsd * Math.max(1, input.usdRub) : safeGram * Math.max(1, input.gramRub);
  const baseRub = Number(baseRubRaw.toFixed(2));
  const priceRub = Math.max(1, ceilSafe(baseRubRaw * safeMarkup));
  const priceGram = Number((safeGram * safeMarkup).toFixed(3));
  const priceStars = Math.max(1, ceilSafe(safeGram * safeMarkup / safeStarGram));
  const discountRatio = Math.min(0.85, Math.max(0, input.discountPercent / 100));
  const officialRaw = discountRatio > 0 ? baseRubRaw / (1 - discountRatio) : baseRubRaw * 1.25;
  const officialRub = Math.max(priceRub, ceilSafe(officialRaw));
  return {
    months: input.months,
    baseRub,
    priceRub,
    priceGram,
    priceStars,
    officialRub
  };
}
function ceilSafe(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.ceil(Number(value.toFixed(9)));
}
function computeRetailStarsQty(qty, markup) {
  const safeMarkup = Number.isFinite(markup) ? Math.max(1, markup) : 1.1;
  return Math.max(1, ceilSafe(Math.max(1, qty) * safeMarkup));
}
function computeOrderMilliGram(qty, unitPriceGram) {
  const safeQty = Number.isFinite(qty) ? Math.max(1, qty) : 1;
  const safePrice = Number.isFinite(unitPriceGram) ? Math.max(1e-4, unitPriceGram) : 1e-3;
  return Math.max(1, ceilSafe(safeQty * safePrice * 1e3));
}
function sanitizeFirstOrderDiscountPercent(discountPercent) {
  if (!Number.isFinite(discountPercent) || discountPercent <= 0) return 0;
  return Math.min(50, Number(discountPercent.toFixed(4)));
}
function applyFirstOrderDiscountKopecks(amountKopecks, discountPercent) {
  const base = Math.max(1, Math.round(amountKopecks));
  const pct = sanitizeFirstOrderDiscountPercent(discountPercent);
  if (pct <= 0) return base;
  return Math.max(1, Math.round(Number((base * (1 - pct / 100)).toFixed(6))));
}
function applyFirstOrderDiscountMilliGram(milliGram, discountPercent) {
  const base = Math.max(1, Math.round(milliGram));
  const pct = sanitizeFirstOrderDiscountPercent(discountPercent);
  if (pct <= 0) return base;
  return Math.max(1, ceilSafe(base * (1 - pct / 100)));
}
function applyFirstOrderDiscountStars(starsAmount, discountPercent) {
  const base = Math.max(1, Math.round(starsAmount));
  const pct = sanitizeFirstOrderDiscountPercent(discountPercent);
  if (pct <= 0) return base;
  return Math.max(1, ceilSafe(base * (1 - pct / 100)));
}
function markupToFeePercent(markup) {
  if (!Number.isFinite(markup) || markup <= 1) return 0;
  return Math.min(900, Number(((markup - 1) * 100).toFixed(2)));
}
function feePercentToMarkup(feePercent) {
  if (!Number.isFinite(feePercent) || feePercent <= 0) return 1;
  const clamped = Math.min(900, Math.max(0, feePercent));
  return Number((1 + clamped / 100).toFixed(6));
}
function normalizeSpendCommissionTiers(raw) {
  if (!Array.isArray(raw)) return [];
  const byMinSpent = /* @__PURE__ */ new Map();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const rec = item;
    const minRaw = Number(rec.minSpentRub);
    const starsRaw = Number(rec.starsFeePercent);
    const premRaw = Number(rec.premiumFeePercent);
    if (!Number.isFinite(minRaw) || minRaw <= 0) continue;
    if (!Number.isFinite(starsRaw) || !Number.isFinite(premRaw)) continue;
    const minSpentRub = Math.round(minRaw * 100) / 100;
    const starsFeePercent = Number(Math.min(900, Math.max(0, starsRaw)).toFixed(2));
    const premiumFeePercent = Number(Math.min(900, Math.max(0, premRaw)).toFixed(2));
    byMinSpent.set(minSpentRub, { minSpentRub, starsFeePercent, premiumFeePercent });
  }
  return [...byMinSpent.values()].sort((a, b) => a.minSpentRub - b.minSpentRub);
}
function computeTariffDiscountPercent(generalFeePercent, effectiveFeePercent) {
  const generalMarkup = feePercentToMarkup(generalFeePercent);
  const effectiveMarkup = feePercentToMarkup(effectiveFeePercent);
  if (generalMarkup <= 0 || effectiveMarkup >= generalMarkup) return 0;
  const pct = (generalMarkup - effectiveMarkup) / generalMarkup * 100;
  return Math.max(0, Number(pct.toFixed(1)));
}
function resolveUserCommission(input) {
  const spentRub = Number.isFinite(input.spentRub) ? Math.max(0, Number(input.spentRub.toFixed(2))) : 0;
  const tiers = normalizeSpendCommissionTiers(input.tiers ?? []);
  let matchedTier = null;
  let nextCandidate = null;
  for (const tier of tiers) {
    if (spentRub >= tier.minSpentRub) {
      matchedTier = tier;
    } else if (!nextCandidate) {
      nextCandidate = tier;
    }
  }
  const genStarsPct = markupToFeePercent(input.generalStarsMarkup);
  const genPremPct = markupToFeePercent(input.generalPremiumMarkup);
  const hasCustomStars = typeof input.customStarsFeeBp === "number" && Number.isFinite(input.customStarsFeeBp);
  const hasCustomPrem = typeof input.customPremiumFeeBp === "number" && Number.isFinite(input.customPremiumFeeBp);
  const starsSource = hasCustomStars ? "personal" : matchedTier ? "spend_tier" : "general";
  const premiumSource = hasCustomPrem ? "personal" : matchedTier ? "spend_tier" : "general";
  const starsFeePercent = hasCustomStars ? Number((Math.min(9e4, Math.max(0, Math.round(input.customStarsFeeBp))) / 100).toFixed(2)) : matchedTier ? matchedTier.starsFeePercent : genStarsPct;
  const premiumFeePercent = hasCustomPrem ? Number((Math.min(9e4, Math.max(0, Math.round(input.customPremiumFeeBp))) / 100).toFixed(2)) : matchedTier ? matchedTier.premiumFeePercent : genPremPct;
  const source = starsSource === "personal" || premiumSource === "personal" ? "personal" : matchedTier ? "spend_tier" : "general";
  const bothPersonal = starsSource === "personal" && premiumSource === "personal";
  const nextTier = !bothPersonal && nextCandidate ? {
    minSpentRub: nextCandidate.minSpentRub,
    remainingRub: Math.max(0, Number((nextCandidate.minSpentRub - spentRub).toFixed(2))),
    starsFeePercent: nextCandidate.starsFeePercent,
    premiumFeePercent: nextCandidate.premiumFeePercent
  } : null;
  return {
    source,
    starsSource,
    premiumSource,
    generalStarsFeePercent: genStarsPct,
    generalPremiumFeePercent: genPremPct,
    starsFeePercent,
    premiumFeePercent,
    starsDiscountPercent: computeTariffDiscountPercent(genStarsPct, starsFeePercent),
    premiumDiscountPercent: computeTariffDiscountPercent(genPremPct, premiumFeePercent),
    starsMarkup: feePercentToMarkup(starsFeePercent),
    premiumMarkup: feePercentToMarkup(premiumFeePercent),
    spentRub,
    currentTierMinRub: matchedTier ? matchedTier.minSpentRub : null,
    nextTier
  };
}
function computeDeliveredOrderRub(order) {
  if (order.productType !== "STARS" && order.productType !== "PREMIUM") return 0;
  const paidKopecks = (order.amountKopecks ?? 0) + (order.balanceAppliedKopecks ?? 0);
  if (paidKopecks > 0) {
    return Number((paidKopecks / 100).toFixed(2));
  }
  const unitRub = Math.max(0, (order.starPriceRub ?? 0) / 1e3);
  const rub = order.productType === "PREMIUM" ? unitRub : Math.max(0, order.starsQty) * unitRub;
  return Number(rub.toFixed(2));
}
function sanitizeFirstDepositBonusPercent(bonusPercent) {
  if (!Number.isFinite(bonusPercent) || bonusPercent <= 0) return 0;
  return Math.min(100, Number(bonusPercent.toFixed(2)));
}
function computeDepositBonusUnits(baseUnits, bonusPercent) {
  const safeBase = Math.max(0, Math.floor(baseUnits));
  const pct = sanitizeFirstDepositBonusPercent(bonusPercent);
  if (safeBase <= 0 || pct <= 0) return 0;
  return Math.max(0, Math.floor(Number((safeBase * (pct / 100)).toFixed(6))));
}
function buildFragmentGramSignature(snapshot) {
  const s = Number(snapshot.baseStarGram).toFixed(6);
  const p3 = Number(snapshot.premium[3].baseGram).toFixed(4);
  const p6 = Number(snapshot.premium[6].baseGram).toFixed(4);
  const p12 = Number(snapshot.premium[12].baseGram).toFixed(4);
  return `s:${s}|p3:${p3}|p6:${p6}|p12:${p12}`;
}
function buildPricingCandidateSignature(snapshot) {
  const gramSig = buildFragmentGramSignature(snapshot);
  const su = Number(snapshot.baseStarUsd).toFixed(6);
  const gr = Number(snapshot.gramRub).toFixed(2);
  const ur = Number(snapshot.usdRub).toFixed(2);
  return `${gramSig}|su:${su}|gr:${gr}|ur:${ur}`;
}
function comparePricingSnapshots(input) {
  const { locked, live } = input;
  const paymentMethod = input.paymentMethod ?? "ton";
  const pctChange = (fromVal, toVal) => fromVal > 0 ? Number(((toVal - fromVal) / fromVal * 100).toFixed(2)) : 0;
  const gramRubDelta = Number((live.gramRub - locked.gramRub).toFixed(2));
  const gramRubDeltaPercent = pctChange(locked.gramRub, live.gramRub);
  const usdRubDelta = Number((live.usdRub - locked.usdRub).toFixed(2));
  const usdRubDeltaPercent = pctChange(locked.usdRub, live.usdRub);
  const fxChanged = Math.abs(gramRubDelta) >= 0.01 || Math.abs(usdRubDelta) >= 0.01;
  const starGramDelta = Number((live.baseStarGram - locked.baseStarGram).toFixed(6));
  const premGramChanged = [3, 6, 12].some(
    (m) => Math.abs(live.premium[m].baseGram - locked.premium[m].baseGram) >= 1e-4
  );
  const fragmentGramChanged = Math.abs(starGramDelta) >= 1e-6 || premGramChanged;
  const lockedStar = computeRetailStarPricing({
    baseStarGram: locked.baseStarGram,
    baseStarUsd: locked.baseStarUsd,
    gramRub: locked.gramRub,
    usdRub: locked.usdRub,
    markup: input.starsMarkup,
    paymentMethod
  });
  const liveStar = computeRetailStarPricing({
    baseStarGram: live.baseStarGram,
    baseStarUsd: live.baseStarUsd,
    gramRub: live.gramRub,
    usdRub: live.usdRub,
    markup: input.starsMarkup,
    paymentMethod
  });
  const star1 = {
    lockedRub: lockedStar.retailRub,
    liveRub: liveStar.retailRub,
    deltaRub: Number((liveStar.retailRub - lockedStar.retailRub).toFixed(4)),
    deltaPercent: pctChange(lockedStar.retailRub, liveStar.retailRub),
    lockedBaseGram: Number(locked.baseStarGram.toFixed(6)),
    liveBaseGram: Number(live.baseStarGram.toFixed(6)),
    deltaBaseGram: starGramDelta
  };
  const locked100 = Number((lockedStar.retailRub * 100).toFixed(2));
  const live100 = Number((liveStar.retailRub * 100).toFixed(2));
  const stars100 = {
    lockedRub: locked100,
    liveRub: live100,
    deltaRub: Number((live100 - locked100).toFixed(2)),
    deltaPercent: pctChange(locked100, live100)
  };
  const premium = [3, 6, 12].map((months) => {
    const lockedTier = computeRetailPremiumTier({
      months,
      baseGram: locked.premium[months].baseGram,
      baseUsd: locked.premium[months].baseUsd,
      discountPercent: locked.premium[months].discountPercent,
      baseStarGram: locked.baseStarGram,
      gramRub: locked.gramRub,
      usdRub: locked.usdRub,
      markup: input.premiumMarkup,
      paymentMethod
    });
    const liveTier = computeRetailPremiumTier({
      months,
      baseGram: live.premium[months].baseGram,
      baseUsd: live.premium[months].baseUsd,
      discountPercent: live.premium[months].discountPercent,
      baseStarGram: live.baseStarGram,
      gramRub: live.gramRub,
      usdRub: live.usdRub,
      markup: input.premiumMarkup,
      paymentMethod
    });
    return {
      months,
      lockedRub: lockedTier.priceRub,
      liveRub: liveTier.priceRub,
      deltaRub: Number((liveTier.priceRub - lockedTier.priceRub).toFixed(2)),
      deltaPercent: pctChange(lockedTier.priceRub, liveTier.priceRub),
      lockedBaseGram: Number(locked.premium[months].baseGram.toFixed(4)),
      liveBaseGram: Number(live.premium[months].baseGram.toFixed(4)),
      deltaBaseGram: Number(
        (live.premium[months].baseGram - locked.premium[months].baseGram).toFixed(4)
      )
    };
  });
  const anyRubChanged = Math.abs(star1.deltaRub) >= 1e-4 || Math.abs(stars100.deltaRub) >= 0.01 || premium.some((p) => p.deltaRub !== 0);
  return {
    hasChanges: fxChanged || fragmentGramChanged || anyRubChanged,
    fxChanged,
    fragmentGramChanged,
    fragmentGramSignature: buildFragmentGramSignature(live),
    candidateSignature: buildPricingCandidateSignature(live),
    gramRubDelta,
    gramRubDeltaPercent,
    usdRubDelta,
    usdRubDeltaPercent,
    star1,
    stars100,
    premium
  };
}
var DEFAULT_PRICING_ALERT_MIN_DELTA_RUB = 1;
function exceedsPricingAlertThreshold(comparison, minDeltaRub = DEFAULT_PRICING_ALERT_MIN_DELTA_RUB) {
  const threshold = Number.isFinite(minDeltaRub) && minDeltaRub >= 0 ? minDeltaRub : DEFAULT_PRICING_ALERT_MIN_DELTA_RUB;
  if (Math.abs(comparison.stars100.deltaRub) > threshold) {
    return true;
  }
  return comparison.premium.some((tier) => Math.abs(tier.deltaRub) > threshold);
}

// src/catalog-pricing.ts
var DEFAULT_CATEGORY_MARKUP_BP = 1500;
var FALLBACK_USD_RUB = 96;
var FALLBACK_GRAM_RUB = 120;
function firstFiniteNumber(...values) {
  for (const val of values) {
    if (typeof val === "number" && Number.isFinite(val)) {
      return val;
    }
  }
  return null;
}
function resolveEffectiveMarkupBp(offerMarkupBp, productMarkupBp, categoryDefaultMarkupBp) {
  const raw = firstFiniteNumber(offerMarkupBp, productMarkupBp, categoryDefaultMarkupBp) ?? DEFAULT_CATEGORY_MARKUP_BP;
  return Math.max(0, Math.round(raw));
}
function computeCatalogOfferPrice(input) {
  const safeUsdRub = Number.isFinite(input.usdRub) && input.usdRub > 0 ? input.usdRub : FALLBACK_USD_RUB;
  const safeGramRub = Number.isFinite(input.gramRub) && input.gramRub > 0 ? input.gramRub : FALLBACK_GRAM_RUB;
  const safeMarkupBp = Number.isFinite(input.markupBp) ? Math.max(0, input.markupBp) : 0;
  const markupMultiplier = 1 + safeMarkupBp / 1e4;
  const safeProviderRateBp = typeof input.providerRateBp === "number" && Number.isFinite(input.providerRateBp) && input.providerRateBp > 0 ? input.providerRateBp : 1e4;
  const safeWholesaleUsdMicros = Number.isFinite(input.wholesaleUsdMicros) ? Math.max(0, input.wholesaleUsdMicros) : 0;
  const isCustomRub = typeof input.customAmountRub === "number" && Number.isFinite(input.customAmountRub) && input.customAmountRub > 0;
  const wholesaleRub = isCustomRub ? input.customAmountRub * (safeProviderRateBp / 1e4) : safeWholesaleUsdMicros / 1e6 * safeUsdRub;
  const baseRub = Math.max(1, ceilSafe(wholesaleRub * markupMultiplier));
  const baseKopecks = toKopecks(baseRub);
  const discountPct = typeof input.firstOrderDiscountPercent === "number" && Number.isFinite(input.firstOrderDiscountPercent) ? Math.max(0, input.firstOrderDiscountPercent) : 0;
  const retailKopecks = discountPct > 0 ? applyFirstOrderDiscountKopecks(baseKopecks, discountPct) : baseKopecks;
  const retailRub = kopecksToRub(retailKopecks);
  const baseMilliGram = Math.max(1, ceilSafe(baseRub / safeGramRub * 1e3));
  const retailMilliGram = discountPct > 0 ? applyFirstOrderDiscountMilliGram(baseMilliGram, discountPct) : baseMilliGram;
  return {
    wholesaleRub: Number(wholesaleRub.toFixed(2)),
    baseRub,
    retailRub,
    retailKopecks,
    baseMilliGram,
    retailMilliGram
  };
}
function evaluateCatalogMarginGuard(input) {
  const safeUsdRub = Number.isFinite(input.usdRub) && input.usdRub > 0 ? input.usdRub : FALLBACK_USD_RUB;
  const safeRetailKopecks = Number.isFinite(input.orderRetailTotalKopecks) ? Math.max(0, Math.floor(input.orderRetailTotalKopecks)) : 0;
  const hasOverride = typeof input.liveWholesaleKopecksOverride === "number" && Number.isFinite(input.liveWholesaleKopecksOverride) && input.liveWholesaleKopecksOverride >= 0;
  const hasMicros = typeof input.liveWholesaleUsdMicros === "number" && Number.isFinite(input.liveWholesaleUsdMicros) && input.liveWholesaleUsdMicros >= 0;
  if (!hasOverride && !hasMicros) {
    return {
      allowed: false,
      liveWholesaleKopecks: safeRetailKopecks + 1,
      deficitKopecks: 1
    };
  }
  const liveWholesaleKopecks = hasOverride ? Math.max(0, ceilSafe(input.liveWholesaleKopecksOverride)) : Math.max(0, ceilSafe(input.liveWholesaleUsdMicros / 1e6 * safeUsdRub * 100));
  const deficitKopecks = Math.max(0, liveWholesaleKopecks - safeRetailKopecks);
  return {
    allowed: deficitKopecks === 0,
    liveWholesaleKopecks,
    deficitKopecks
  };
}
function xpForCatalogOrder(retailTotalKopecks) {
  const rub = Math.max(0, retailTotalKopecks) / 100;
  return Math.max(5, Math.floor(rub / 2));
}

// src/daily.ts
var DAILY_XP = 50;
var STREAK_DAY_XP = [10, 10, 15, 15, 20, 25, 50];
function utcDayIndex(d) {
  return Math.floor(d.getTime() / 864e5);
}
function resolveDailyClaim(lastClaim, currentStreak, now) {
  const today = utcDayIndex(now);
  if (lastClaim) {
    const last = utcDayIndex(lastClaim);
    if (last >= today) {
      return { alreadyClaimed: true, streak: Math.max(1, currentStreak), xpAwarded: 0 };
    }
    if (last === today - 1) {
      return { alreadyClaimed: false, streak: currentStreak + 1, xpAwarded: DAILY_XP };
    }
  }
  return { alreadyClaimed: false, streak: 1, xpAwarded: DAILY_XP };
}
function canClaimDaily(lastClaim, now) {
  if (!lastClaim) return true;
  return utcDayIndex(lastClaim) < utcDayIndex(now);
}

// src/format.ts
var fmt = (n) => Math.round(n).toLocaleString("ru-RU");
var fmt1 = (n) => (Math.round(n * 10) / 10).toLocaleString("ru-RU", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});
var fmtRub = (rub) => rub.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
var fmtGram = (gram) => (Math.round(gram * 100) / 100).toLocaleString("ru-RU", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});
var maskUsername = (value, hidden) => hidden ? "*****" : value?.trim() || "\u041A\u0430\u043F\u0438\u0442\u0430\u043D";

// src/levels.ts
var DEFAULT_FEE_LADDER = {
  start: 5,
  step: 0.5,
  floor: 3
};
var LEVEL_TABLE = [
  { lvl: 1, reqXp: 0, title: "\u0418\u043A\u0440\u0438\u043D\u043A\u0430" },
  { lvl: 2, reqXp: 250, title: "\u041C\u0430\u043B\u0451\u043A" },
  { lvl: 3, reqXp: 650, title: "\u041F\u043E\u043B\u043E\u0441\u0430\u0442\u0438\u043A" },
  { lvl: 4, reqXp: 1200, title: "\u0420\u0438\u0444\u043E\u0432\u044B\u0439 \u0441\u0442\u0440\u0430\u0436" },
  { lvl: 5, reqXp: 2200, title: "\u041C\u043E\u0440\u0441\u043A\u043E\u0439 \u0432\u043E\u043B\u043A" },
  { lvl: 6, reqXp: 3800, title: "\u041F\u043E\u0432\u0435\u043B\u0438\u0442\u0435\u043B\u044C \u0432\u043E\u043B\u043D" },
  { lvl: 7, reqXp: 6200, title: "\u0413\u043B\u0443\u0431\u0438\u043D\u043D\u044B\u0439 \u0445\u0438\u0449\u043D\u0438\u043A" },
  { lvl: 8, reqXp: 9500, title: "\u0425\u0440\u0430\u043D\u0438\u0442\u0435\u043B\u044C \u0410\u0442\u043B\u0430\u043D\u0442\u0438\u0434\u044B" },
  { lvl: 9, reqXp: 14e3, title: "\u0417\u0432\u0451\u0437\u0434\u043D\u044B\u0439 \u041B\u0435\u0432\u0438\u0430\u0444\u0430\u043D" },
  { lvl: 10, reqXp: 2e4, title: "\u041F\u043E\u0441\u0435\u0439\u0434\u043E\u043D" }
];
function stageForLevel(lvl) {
  return lvl <= 2 ? 1 : lvl <= 4 ? 2 : lvl <= 6 ? 3 : 4;
}
function feeForLevel(lvl, ladder = DEFAULT_FEE_LADDER) {
  const raw = ladder.start - ladder.step * (lvl - 1);
  return Math.max(ladder.floor, Math.round(raw * 10) / 10);
}
function levelInfo(xp, ladder = DEFAULT_FEE_LADDER) {
  let idx = 0;
  for (let k = 0; k < LEVEL_TABLE.length; k++) {
    if (xp >= LEVEL_TABLE[k].reqXp) idx = k;
  }
  const cur = LEVEL_TABLE[idx];
  const next = LEVEL_TABLE[idx + 1] ?? null;
  const span = next ? next.reqXp - cur.reqXp : 1;
  const into = xp - cur.reqXp;
  return {
    lvl: cur.lvl,
    title: cur.title,
    feePercent: feeForLevel(cur.lvl, ladder),
    reqXp: cur.reqXp,
    next,
    into,
    span,
    progress: next ? Math.min(1, into / span) : 1,
    isMax: !next
  };
}
function feeLadder(ladder = DEFAULT_FEE_LADDER) {
  return LEVEL_TABLE.map((l) => ({
    lvl: l.lvl,
    title: l.title,
    feePercent: feeForLevel(l.lvl, ladder)
  }));
}

// src/referrals.ts
var REFERRAL_GENERATIONS_CONFIG_KEY = "REFERRAL_GENERATIONS_CONFIG";
function normalizeReferralGenerationsConfig(raw, fallbackL1 = 5) {
  const inviterBonusXp = Math.max(0, Math.round(Number(raw?.inviterBonusXp ?? 200) || 0));
  const inviteeDiscountPercent = Math.min(
    50,
    Math.max(0, Number(raw?.inviteeDiscountPercent ?? 2) || 0)
  );
  const hasExplicitPercents = Array.isArray(raw?.generationPercents) && raw.generationPercents.length > 0;
  const requestedCount = raw?.generationsCount !== void 0 ? Math.round(Number(raw.generationsCount)) : hasExplicitPercents ? raw.generationPercents.length : 2;
  const generationsCount = Math.min(
    10,
    Math.max(1, Number.isFinite(requestedCount) ? requestedCount : 2)
  );
  const seedPercents = hasExplicitPercents ? raw.generationPercents : [
    raw?.commissionPercentL1 !== void 0 ? Number(raw.commissionPercentL1) : fallbackL1,
    raw?.commissionPercentL2 !== void 0 ? Number(raw.commissionPercentL2) : 1.5
  ];
  const generationPercents = [];
  for (let i = 0; i < generationsCount; i++) {
    const candidate = seedPercents[i];
    const num = typeof candidate === "number" && Number.isFinite(candidate) ? candidate : 0;
    const clamped = Number(Math.min(50, Math.max(0, num)).toFixed(2));
    generationPercents.push(clamped);
  }
  return {
    inviterBonusXp,
    inviteeDiscountPercent,
    generationsCount,
    generationPercents,
    commissionPercentL1: generationPercents[0] ?? 0,
    commissionPercentL2: generationPercents[1] ?? 0
  };
}
function computeGenerationRewards(input) {
  if (input.grandKopecks <= 0 || input.generationPercents.length === 0) return [];
  const visited = /* @__PURE__ */ new Set([input.buyerId]);
  const steps = [];
  let cursor = input.buyerId;
  for (let idx = 0; idx < input.generationPercents.length; idx++) {
    const parentId = input.resolveReferrerId(cursor);
    if (!parentId || visited.has(parentId)) break;
    visited.add(parentId);
    const pct = input.generationPercents[idx] ?? 0;
    const amountKopecks = pct > 0 ? Math.round(input.grandKopecks * pct / 100) : 0;
    if (amountKopecks > 0) {
      steps.push({
        level: idx + 1,
        inviterUserId: parentId,
        percent: pct,
        amountKopecks
      });
    }
    cursor = parentId;
  }
  return steps;
}

// src/roles.ts
var PLATFORM_ROLES = ["superadministrator", "administrator", "support"];
var PLATFORM_PERMISSIONS = [
  "admin.read",
  "order.manage",
  "order.force_match",
  "payment.match",
  "admin.user.manage",
  "admin.user.suspend",
  "admin.tamagotchi.manage",
  "admin.role.manage",
  "support.manage",
  "broadcast.manage",
  "banners.manage",
  "balance.admin_adjust",
  "config.economy.update",
  "config.referrals.update",
  "treasury.read",
  "games.manage",
  "quests.manage",
  "fragment.session.manage",
  "tbank.session.manage",
  "audit.read"
];
var ROLE_PERMISSION_MATRIX = {
  superadministrator: PLATFORM_PERMISSIONS,
  administrator: PLATFORM_PERMISSIONS.filter((p) => p !== "admin.role.manage"),
  support: [
    "admin.read",
    "order.manage",
    "admin.user.manage",
    "admin.user.suspend",
    "admin.tamagotchi.manage",
    "support.manage",
    "audit.read"
  ]
};
function permissionsForRoles(roles) {
  const set = /* @__PURE__ */ new Set();
  for (const role of roles) {
    for (const perm of ROLE_PERMISSION_MATRIX[role] ?? []) {
      set.add(perm);
    }
  }
  return Array.from(set);
}
function hasPermission(roles, permission) {
  return permissionsForRoles(roles).includes(permission);
}

// src/tamagotchi.ts
var HUNGER_DECAY_PER_HOUR = 100 / 48;
var HAPPINESS_DECAY_PER_HOUR = 100 / 36;
var HEALTH_DECAY_PER_HOUR = 100 / 48;
var FEED_COOLDOWN_MS = 4 * 36e5;
var PLAY_COOLDOWN_MS = 2 * 36e5;
var FEED_XP = 15;
var PLAY_XP = 5;
var REVIVE_COST_RUB = 49;
var REVIVE_COST_KOPECKS = REVIVE_COST_RUB * 100;
var REVIVE_COST_GRAM = 1;
var REVIVE_COST_MILLI_GRAM = REVIVE_COST_GRAM * 1e3;
function clamp100(n) {
  return Math.max(0, Math.min(100, Math.round(n)));
}
function computeRawFishBars(snapshot, now) {
  if (snapshot.health <= 0) {
    return { hunger: 0, happiness: 0, health: 0 };
  }
  const dtHours = Math.max(0, (now.getTime() - snapshot.updatedAt.getTime()) / 36e5);
  const h0 = Math.max(0, Math.min(100, snapshot.hunger));
  const p0 = Math.max(0, Math.min(100, snapshot.happiness));
  const hp0 = Math.max(0, Math.min(100, snapshot.health));
  const hunger = Math.max(0, h0 - dtHours * HUNGER_DECAY_PER_HOUR);
  const happiness = Math.max(0, p0 - dtHours * HAPPINESS_DECAY_PER_HOUR);
  const tHungerZero = h0 / HUNGER_DECAY_PER_HOUR;
  const tHappyZero = p0 / HAPPINESS_DECAY_PER_HOUR;
  const hungerZeroHours = Math.max(0, dtHours - tHungerZero);
  const happyZeroHours = Math.max(0, dtHours - tHappyZero);
  const healthLoss = (hungerZeroHours + happyZeroHours) * HEALTH_DECAY_PER_HOUR;
  const health = Math.max(0, hp0 - healthLoss);
  if (health <= 0) {
    return { hunger: 0, happiness: 0, health: 0 };
  }
  return { hunger, happiness, health };
}
function computeFishState(snapshot, now = /* @__PURE__ */ new Date()) {
  const raw = computeRawFishBars(snapshot, now);
  const hunger = clamp100(raw.hunger);
  const happiness = clamp100(raw.happiness);
  const health = clamp100(raw.health);
  const isDead = health <= 0;
  let status;
  if (isDead) {
    status = "dead";
  } else if (health < 40 || hunger === 0 && happiness === 0) {
    status = "critical";
  } else if (hunger < 35 || happiness < 35) {
    status = "hungry";
  } else if (health >= 75 && hunger >= 75 && happiness >= 75) {
    status = "thriving";
  } else {
    status = "ok";
  }
  const xpMultiplier = status === "thriving" ? 1.2 : 1;
  const nextFeedMs = snapshot.lastFedAt ? snapshot.lastFedAt.getTime() + FEED_COOLDOWN_MS : 0;
  const canFeed = !isDead && now.getTime() >= nextFeedMs;
  const nextFeedAt = !isDead && !canFeed ? new Date(nextFeedMs).toISOString() : null;
  const nextPlayMs = snapshot.lastPlayedAt ? snapshot.lastPlayedAt.getTime() + PLAY_COOLDOWN_MS : 0;
  const canPlay = !isDead && now.getTime() >= nextPlayMs;
  const nextPlayAt = !isDead && !canPlay ? new Date(nextPlayMs).toISOString() : null;
  return {
    hunger,
    happiness,
    health,
    status,
    isDead,
    xpMultiplier,
    canFeed,
    nextFeedAt,
    canPlay,
    nextPlayAt,
    reviveCostRub: REVIVE_COST_RUB,
    reviveCostGram: REVIVE_COST_GRAM
  };
}
function applyFeedFish(snapshot, now = /* @__PURE__ */ new Date()) {
  const cur = computeFishState(snapshot, now);
  if (cur.isDead) return { ...snapshot, hunger: 0, happiness: 0, health: 0, updatedAt: now };
  return {
    hunger: clamp100(cur.hunger + 40),
    happiness: clamp100(cur.happiness + 15),
    health: clamp100(cur.health + 10),
    updatedAt: now,
    lastFedAt: now,
    lastPlayedAt: snapshot.lastPlayedAt
  };
}
function applyPlayFish(snapshot, now = /* @__PURE__ */ new Date()) {
  const cur = computeFishState(snapshot, now);
  if (cur.isDead) return { ...snapshot, hunger: 0, happiness: 0, health: 0, updatedAt: now };
  return {
    hunger: cur.hunger,
    happiness: clamp100(cur.happiness + 25),
    health: clamp100(cur.health + 5),
    updatedAt: now,
    lastFedAt: snapshot.lastFedAt,
    lastPlayedAt: now
  };
}
function healedFishSnapshot(now = /* @__PURE__ */ new Date(), prev) {
  return {
    hunger: 100,
    happiness: 100,
    health: 100,
    updatedAt: now,
    lastFedAt: prev?.lastFedAt ?? null,
    lastPlayedAt: prev?.lastPlayedAt ?? null
  };
}

// src/xp.ts
var XP_PER_STAR = 1;
var XP_PER_REFERRAL = 200;
var XP_PER_PREMIUM_MONTH = 100;
function xpForPurchase(stars) {
  return Math.max(0, Math.floor(stars)) * XP_PER_STAR;
}
function xpForPremium(months) {
  return Math.max(0, Math.floor(months)) * XP_PER_PREMIUM_MONTH;
}
export {
  BRAINROTS,
  CASES,
  CASE_ONLY_LVL,
  CRASH_SPEED_MS,
  DAILY_XP,
  DEFAULT_BRAINROT,
  DEFAULT_FEE_LADDER,
  DEFAULT_PRICING_ALERT_MIN_DELTA_RUB,
  FEED_COOLDOWN_MS,
  FEED_XP,
  HAPPINESS_DECAY_PER_HOUR,
  HEALTH_DECAY_PER_HOUR,
  HOUSE_EDGE,
  HUNGER_DECAY_PER_HOUR,
  LEVEL_TABLE,
  MATCH_OVERPAY_MAX_KOPECKS,
  MATCH_UNDERPAY_MAX_KOPECKS,
  MAX_BET_KOPECKS,
  MAX_CRASH_MULTIPLIER_BP,
  MIN_BET_KOPECKS,
  PLATFORM_PERMISSIONS,
  PLATFORM_ROLES,
  PLAY_COOLDOWN_MS,
  PLAY_XP,
  QTY_LIMITS,
  REFERRAL_GENERATIONS_CONFIG_KEY,
  REVIVE_COST_GRAM,
  REVIVE_COST_KOPECKS,
  REVIVE_COST_MILLI_GRAM,
  REVIVE_COST_RUB,
  ROLE_PERMISSION_MATRIX,
  ROUND_RESULT_SECONDS,
  ROUND_WAIT_SECONDS,
  STARS_MIN,
  STARTER_BRAINROT_IDS,
  STEP_UP_ACTIONS,
  STEP_UP_TTL_MS,
  STREAK_DAY_XP,
  TAU,
  XP_PER_PREMIUM_MONTH,
  XP_PER_REFERRAL,
  XP_PER_STAR,
  applyFeedFish,
  applyFirstOrderDiscountKopecks,
  applyFirstOrderDiscountMilliGram,
  applyFirstOrderDiscountStars,
  applyPlayFish,
  brainrotName,
  buildFragmentGramSignature,
  buildPricingCandidateSignature,
  canClaimDaily,
  ceilSafe,
  clampQty,
  comparePricingSnapshots,
  computeCaseStats,
  computeCatalogOfferPrice,
  computeDeliveredOrderRub,
  computeDepositBonusUnits,
  computeFishState,
  computeGenerationRewards,
  computeOrderMilliGram,
  computePrice,
  computeRawFishBars,
  computeRetailPremiumTier,
  computeRetailStarPricing,
  computeRetailStarsQty,
  computeTariffDiscountPercent,
  currentMultiplier,
  evaluateCatalogMarginGuard,
  exceedsPricingAlertThreshold,
  feeForLevel,
  feeLadder,
  feePercentToMarkup,
  fmt,
  fmt1,
  fmtGameRub,
  fmtGram,
  fmtRub,
  formatMultiplier,
  getBrainrot,
  getCase,
  gramToMilli,
  hasPermission,
  healedFishSnapshot,
  isStarterBrainrot,
  isStepUpAction,
  isUnlocked,
  isWithinMatchWindow,
  kopecksToRub,
  levelInfo,
  markupToFeePercent,
  maskUsername,
  milliToGram,
  multiplierAtMs,
  normalizeCompanionId,
  normalizeReferralGenerationsConfig,
  normalizeSpendCommissionTiers,
  permissionsForRoles,
  pickLoot,
  pickUniqueAmountKopecks,
  resolveDailyClaim,
  resolveEffectiveMarkupBp,
  resolveUserCommission,
  rubToMilliGram,
  rubToStars,
  sahurStats,
  sanitizeFirstDepositBonusPercent,
  sanitizeFirstOrderDiscountPercent,
  stageForLevel,
  starsToMilliGram,
  toKopecks,
  utcDayIndex,
  xpForCatalogOrder,
  xpForPremium,
  xpForPurchase
};
